import type { SettingsSubviewProps, ThemeOption } from './Settings.types';
import type { ReactElement } from 'react';

import { Flex } from '@rango-dev/whale';
import clsx from 'clsx';
import { Moon, Sun, SunMoon } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { changeTheme } from '../../state/SettingsSlice';

import styles from './Settings.module.css';
import { SettingsOption } from './Settings.Option';
import { SettingsSubviewHeader } from './Settings.SubviewHeader';

const THEME_ICON_SIZE = 20;

export function useThemeOptions(): ThemeOption[] {
  const { t } = useTranslation();

  return [
    {
      value: 'light',
      title: t('Light'),
      description: t('Always use the light theme'),
      icon: <Sun size={THEME_ICON_SIZE} />,
    },
    {
      value: 'dark',
      title: t('Dark'),
      description: t('Always use the dark theme'),
      icon: <Moon size={THEME_ICON_SIZE} />,
    },
    {
      value: 'auto',
      title: t('System'),
      description: t('Match your device setting'),
      icon: <SunMoon size={THEME_ICON_SIZE} />,
    },
  ];
}

export function SettingsTheme(props: SettingsSubviewProps): ReactElement {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.settings.theme);
  const options = useThemeOptions();

  return (
    <Flex direction="column" gap="3">
      <SettingsSubviewHeader
        title={t('Theme')}
        backButtonId="settings-theme-back-btn"
        onBack={props.onBack}
      />
      <Flex direction="column" gap="2">
        {options.map((option) => {
          const selected = theme === option.value;

          return (
            <SettingsOption
              key={option.value}
              id={`settings-theme-${option.value}-item`}
              title={option.title}
              description={option.description}
              selected={selected}
              onSelect={() => dispatch(changeTheme(option.value))}
              media={
                <Flex
                  align="center"
                  p="2"
                  className={clsx(styles.optionTile, selected && styles.optionTileSelected)}>
                  {option.icon}
                </Flex>
              }
            />
          );
        })}
      </Flex>
    </Flex>
  );
}
